// src/pages/school_exams/template_builder/TemplateBuilder.js
import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";

import api from "../../../api";
import Toolbar from "./Toolbar";
import Canvas from "./Canvas";
import PropertiesPanel from "./PropertiesPanel";
import { buildTemplateJSON } from "./utils/templateJsonBuilder";

const PAGE_WIDTH = 794;   // px
const PAGE_HEIGHT = 1123; // px

const TemplateBuilder = ({ examId }) => {
  // DESIGN ELEMENTS
  const [elements, setElements] = useState([]);

  // BACKGROUND
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [backgroundServerFile, setBackgroundServerFile] = useState(null);

  // TEMPLATE INFO
  const [templateName, setTemplateName] = useState("");

  // VIEW
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);

  // SELECTION
  const [selectedIds, setSelectedIds] = useState([]);
  const hasSelection = selectedIds.length > 0;
  const hasMultipleSelection = selectedIds.length > 1;

  const anyLocked = useMemo(
    () => elements.some((e) => selectedIds.includes(e.id) && e.locked),
    [elements, selectedIds]
  );

  /* ------------------------------------
   * BASIC ELEMENT ADD
   * ---------------------------------- */
  const addElement = (type) => {
    const base = {
      id: uuid(),
      type,
      x: PAGE_WIDTH / 2 - 80,
      y: 180,
      w: 200,
      h: 40,
      rotation: 0,
      locked: false,
    };

    let el = { ...base };

    switch (type) {
      case "text":
        el = {
          ...base,
          text: "Sample Text",
          fontSize: 18,
          bold: false,
          align: "left",
          color: "#111827",
          bindKey: "",
        };
        break;

      case "photo":
        el = {
          ...base,
          w: 120,
          h: 150,
          borderWidth: 1,
          borderRadius: 4,
          bindKey: "student_photo",
        };
        break;

      case "line":
        el = { ...base, w: 400, h: 8, thickness: 2, color: "#000000" };
        break;

      case "rect":
        el = {
          ...base,
          fill: "rgba(0,0,0,0.05)",
          stroke: "#000000",
          strokeWidth: 1,
        };
        break;

      case "circle":
        el = {
          ...base,
          fill: "rgba(0,0,0,0.05)",
          stroke: "#000000",
          strokeWidth: 1,
        };
        break;

      case "table":
        el = {
          ...base,
          w: 520,
          h: 260,
          headerHeight: 32,
          rowHeight: 26,
          fontSize: 11,
          headerBgColor: "#00c851",
          headerTextColor: "#000000",
          rowBgColor: "#ffffff",
          altRowBgColor: "#f9fafb",
          borderColor: "#000000",
          borderWidth: 0.5,
          columns: [
            { id: uuid(), label: "Subject", key: "subject", width: 150 },
            { id: uuid(), label: "Written", key: "written", width: 80 },
            { id: uuid(), label: "Oral", key: "oral", width: 80 },
            { id: uuid(), label: "Total", key: "total", width: 80 },
            { id: uuid(), label: "Obtained", key: "obtained", width: 80 },
            { id: uuid(), label: "Grade", key: "grade", width: 80 },
          ],
          rows: [
            { id: uuid(), cells: ["Subject 1", "", "", "", "", ""] },
            { id: uuid(), cells: ["Subject 2", "", "", "", "", ""] },
          ],
          bindKey: "subjects",
        };
        break;

      default:
        return;
    }

    setElements((prev) => [...prev, el]);
    setSelectedIds([el.id]);
  };

  /* ------------------------------------
   * ADD TEXT UTIL (for quick blocks)
   * ---------------------------------- */
  const addText = (text, bindKey = "", x = 100, y = 100) => {
    const el = {
      id: uuid(),
      type: "text",
      text,
      bindKey,
      x,
      y,
      w: 260,
      h: 32,
      fontSize: bindKey ? 18 : 16,
      bold: !!bindKey,
      color: "#000000",
      align: "left",
      locked: false,
    };

    setElements((prev) => [...prev, el]);
    setSelectedIds([el.id]);
  };

  /* ------------------------------------
   * QUICK BLOCKS (School header, Student info etc.)
   * ---------------------------------- */
  const addQuickBlock = (type) => {
    switch (type) {
      case "school_header":
        addText("SCHOOL NAME", "school_name", 260, 40);
        addText("Address Line", "school_address", 260, 80);
        break;

      case "exam_info":
        addText("EXAM TITLE", "exam_title", 260, 120);
        break;

      case "student_info":
        addText("Name of Student :", "", 60, 200);
        addText("STUDENT NAME", "student_name", 260, 200);

        addText("Class :", "", 60, 240);
        addText("CLASS NAME", "class_name", 260, 240);

        addText("Roll No :", "", 60, 280);
        addText("ROLL NO", "roll_no", 260, 280);

        addText("Father Name :", "", 60, 320);
        addText("FATHER NAME", "father_name", 260, 320);

        addText("Mother Name :", "", 60, 360);
        addText("MOTHER NAME", "mother_name", 260, 360);

        addText("Date of Birth :", "", 60, 400);
        addText("DATE OF BIRTH", "dob", 260, 400);
        break;

      case "result_summary":
        addText("Percentage :", "", 60, 700);
        addText("PERCENTAGE", "percentage", 260, 700);
        break;

      case "photo":
        addElement("photo");
        break;

      default:
        break;
    }
  };

  /* ------------------------------------
   * UPDATE / DELETE / DUPLICATE / LOCK
   * ---------------------------------- */
  const updateElement = (id, changes) => {
    setElements((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...changes } : e))
    );
  };

  const updateMultiple = (ids, changes) => {
    setElements((prev) =>
      prev.map((el) =>
        ids.includes(el.id)
          ? { ...el, ...(typeof changes === "function" ? changes(el) : changes) }
          : el
      )
    );
  };

  const deleteSelected = () => {
    if (!selectedIds.length) return;
    setElements((prev) => prev.filter((el) => !selectedIds.includes(el.id)));
    setSelectedIds([]);
  };

  const duplicateSelected = () => {
    if (!selectedIds.length) return;

    const clones = elements
      .filter((e) => selectedIds.includes(e.id))
      .map((e) => ({
        ...e,
        id: uuid(),
        x: e.x + 20,
        y: e.y + 20,
        locked: false,
      }));

    setElements((prev) => [...prev, ...clones]);
  };

  const toggleLockSelected = () => {
    if (!selectedIds.length) return;
    updateMultiple(selectedIds, (el) => ({ locked: !el.locked }));
  };

  /* ------------------------------------
   * ALIGN (toolbar buttons)
   * ---------------------------------- */
  const alignSelected = (mode) => {
    if (!selectedIds.length) return;

    // একাধিক element align করলে একে অন্যের সাথে,
    // আর single element হলে center_x / center_y page অনুযায়ী।
    if (selectedIds.length === 1) {
      const id = selectedIds[0];
      setElements((prev) =>
        prev.map((el) => {
          if (el.id !== id) return el;
          switch (mode) {
            case "center_h":
              return { ...el, x: PAGE_WIDTH / 2 - el.w / 2 };
            case "center_v":
              return { ...el, y: PAGE_HEIGHT / 2 - el.h / 2 };
            default:
              return el;
          }
        })
      );
      return;
    }

    const selected = elements.filter((el) => selectedIds.includes(el.id));
    const minX = Math.min(...selected.map((e) => e.x));
    const maxRight = Math.max(...selected.map((e) => e.x + e.w));
    const minY = Math.min(...selected.map((e) => e.y));
    const maxBottom = Math.max(...selected.map((e) => e.y + e.h));

    updateMultiple(selectedIds, (el) => {
      switch (mode) {
        case "left":
          return { x: minX };
        case "right":
          return { x: maxRight - el.w };
        case "top":
          return { y: minY };
        case "bottom":
          return { y: maxBottom - el.h };
        case "center_h":
          return { x: PAGE_WIDTH / 2 - el.w / 2 };
        case "center_v":
          return { y: PAGE_HEIGHT / 2 - el.h / 2 };
        default:
          return el;
      }
    });
  };

  /* ------------------------------------
   * LAYER CONTROLS
   * ---------------------------------- */
  const moveLayer = (dir) => {
    setElements((prev) => {
      const arr = [...prev];
      selectedIds.forEach((id) => {
        const i = arr.findIndex((e) => e.id === id);
        if (i === -1) return;

        if (dir === "up" && i < arr.length - 1) {
          [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
        } else if (dir === "down" && i > 0) {
          [arr[i], arr[i - 1]] = [arr[i - 1], arr[i]];
        } else if (dir === "top") {
          const [it] = arr.splice(i, 1);
          arr.push(it);
        } else if (dir === "bottom") {
          const [it] = arr.splice(i, 1);
          arr.unshift(it);
        }
      });
      return arr;
    });
  };

  /* ------------------------------------
   * BACKGROUND UPLOAD
   * ---------------------------------- */
  const handleBackgroundUpload = async (file) => {
    if (!file) return;

    setBackgroundPreview(URL.createObjectURL(file));

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await api.post(
        "/school-exam-templates/upload-background",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (res.data?.success && res.data.file) {
        setBackgroundServerFile(res.data.file);
        toast.success("Background uploaded.");
      } else {
        toast.error("Upload failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload error.");
    }
  };

  /* ------------------------------------
   * SAVE (React → CI3)
   * ---------------------------------- */
  const handleSave = async () => {
    if (!examId) return toast.error("Select an exam.");
    if (!templateName.trim()) return toast.error("Enter a template name.");
    if (!backgroundServerFile) return toast.error("Upload a background.");

    const json = buildTemplateJSON(
      backgroundServerFile,
      elements,
      templateName
    );

    try {
      const res = await api.post("/school-exam-templates/save", {
        exam_id: examId,
        template_name: templateName,
        json_data: JSON.stringify(json),
        background_image: backgroundServerFile,
      });

      res.data?.success
        ? toast.success("Template saved.")
        : toast.error("Save failed.");
    } catch (err) {
      console.error(err);
      toast.error("Server error saving template.");
    }
  };

  /* ------------------------------------
   * LOAD (CI3 → React)
   * ---------------------------------- */
  const handleLoad = async () => {
    if (!examId) return toast.error("Select an exam.");

    try {
      const res = await api.get(`/school-exam-templates/get?exam_id=${examId}`);

      if (!res.data?.success || !res.data.data) {
        return toast.info("Template not found.");
      }

      const t = res.data.data;
      let parsed = t.json_data;

      if (typeof parsed === "string") parsed = JSON.parse(parsed);

      setTemplateName(parsed.meta?.template_name || t.template_name || "");
      setElements(parsed.elements || []);

      let bg = parsed.background?.file || t.background_image || null;
      const BASE = "https://dgps.co.in";

      if (bg && !bg.startsWith("http")) {
        bg = `${BASE}/${bg}`;
      }

      setBackgroundPreview(bg);
      setBackgroundServerFile(bg);

      toast.success("Template loaded.");
    } catch (err) {
      console.error(err);
      toast.error("Load error.");
    }
  };

  /* ------------------------------------
   * RENDER
   * ---------------------------------- */
  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <Toolbar
        addElement={addElement}
        addQuickBlock={addQuickBlock}
        onSave={handleSave}
        onLoad={handleLoad}
        deleteSelected={deleteSelected}
        moveLayer={moveLayer}
        duplicateSelected={duplicateSelected}
        toggleLockSelected={toggleLockSelected}
        alignSelected={alignSelected}
        onBackgroundUpload={handleBackgroundUpload}
        templateName={templateName}
        setTemplateName={setTemplateName}
        zoom={zoom}
        setZoom={setZoom}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        hasSelection={hasSelection}
        hasMultipleSelection={hasMultipleSelection}
        anyLocked={anyLocked}
      />

      <Canvas
        background={backgroundPreview}
        elements={elements}
        setElements={setElements}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        zoom={zoom}
        showGrid={showGrid}
      />

      <PropertiesPanel
        selected={
          selectedIds.length === 1
            ? elements.find((el) => el.id === selectedIds[0])
            : null
        }
        updateElement={updateElement}
        deleteElement={deleteSelected}
      />
    </Box>
  );
};

export default TemplateBuilder;
